import {
  Action,
  AnyAction,
  Dispatch,
  Middleware,
  Observable,
  Reducer,
  Store,
  Unsubscribe,
} from 'redux';
import { UnknownAction } from '@reduxjs/toolkit';
import { ChangeDetectorRef } from '@angular/core';
import { App } from '@ps20-frontend/frontend-models';
import { PS20Store } from './models';
import { GlobalEventDispatcher } from './globalEventDispatcher';
import { EventHistoryQueue } from './eventHistoryQueue';
import { GlobalActions } from './global-actions';

/**
 * Damit Änderungen am Store von außen über den GlobalEventDispatcher automatisch in der Oberfläche sichtbar sind,
 * muss nach jeder Action ein tick() über die ngRefreshMiddleware ausgeführt werden.
 * Nach dem Bootstrap der Anwendung muss in der app.module.ts die appRef über bindApp gesetzt werden.
 *
 */
export class NgRefreshStore<S, A extends Action = AnyAction> implements PS20Store<S, A> {
  dispatch: Dispatch<A>;
  observable: Observable<S>;
  globalEventDispatcherRef: GlobalEventDispatcher;

  private readonly eventQueue: EventHistoryQueue = new EventHistoryQueue();
  private changeDetectorRef: ChangeDetectorRef;
  private delegate: Store<S>;

  [Symbol.observable](): Observable<S> {
    return this.observable;
  }

  constructor(private readonly appId: App) {}

  getDelegate(): Store<S> {
    return this.delegate;
  }

  setDelegate(delegate: Store<S>): void {
    this.delegate = delegate;
    this.dispatch = delegate.dispatch;
  }

  getState(): S {
    return this.delegate.getState();
  }

  subscribe(listener: () => void): Unsubscribe {
    return this.delegate.subscribe(listener);
  }

  replaceReducer(nextReducer: Reducer<S, A>): void {
    this.delegate.replaceReducer(nextReducer as Reducer<S, AnyAction>);
  }

  bindApp(changeDetectorRef: ChangeDetectorRef): void {
    this.changeDetectorRef = changeDetectorRef;
  }

  unbindApp(): void {
    this.changeDetectorRef = null;
  }

  ngRefreshMiddleware: Middleware<Record<string, never>, S, Dispatch<UnknownAction>> =
    (<D extends Dispatch<A>>() =>
    (next: D) => {
      return (action: A): A => {
        let result = action;

        try {
          this.eventQueue.addActionToEventHistory(action);
          result = next(action);

          if (this.changeDetectorRef) {
            this.changeDetectorRef.detectChanges();
          }
        } catch (e) {
          if (
            e instanceof Error &&
            e.message.includes('ApplicationRef.tick is called recursively')
          ) {
            this.unbindApp(); // um Endlosschleife zu verhindern
          }

          const globalEventDispatcher = GlobalEventDispatcher.getInstance();
          if (globalEventDispatcher) {
            globalEventDispatcher.dispatch(
              new GlobalActions().logError(this.appId, e instanceof Error ? e.message : String(e), this.getErrorMessage()),
            );
          }
        }

        return result;
      };
    }) as Middleware<Record<string, never>, S>;

  private getErrorMessage(): string {
    return (
      'ngRefreshMiddleware: Error during dispatch last events:' + this.eventQueue.dumpHistory()
    );
  }
}
